/** @jsxImportSource @emotion/react */

import React from "react";
import Immutable from "immutable";
import {css} from "@emotion/react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "js/common/views/tooltips";
import Cube19Dialog from "js/common/views/dialog";
import CubeTextField from "js/common/views/inputs/text-field";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import {TextButton} from "js/common/views/inputs/buttons";
import ResizeObserver from "resize-observer-polyfill";
import {stripEmoji} from "js/common/utils/strings";
import {TabLoadStatus} from "js/dashboards/tab-cache";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTvRetro} from "@fortawesome/pro-regular-svg-icons";
import * as Colors from "js/common/cube19-colors";
import PresentationSplitButton from "js/dashboards/toolbar/presentation-split-button";
import CopyTabDialog from "js/dashboards/toolbar/copy-tab-dialog";
import {isInIframe} from "js/dashboards/full-screen";

const tabBarContainerStyle = (theme) => css`
  display: flex;
  align-items: center;
  padding: 0 1.5rem;
  border-bottom: 2px solid ${theme.palette.border.light};
  background-color: ${theme.palette.background.card};
`;

const tabsStyle = css`
  min-width: 0;
  min-height: 40px;
`;

const tabStyle = (theme, padLeft) => css`
  min-height: 40px;
  padding: 0 0 0 ${padLeft ? "8px" : "0"};
  margin: 0 8px;
  text-transform: none;
  font-size: 0.9rem;
  overflow: visible;
`;

const tabNameStyle = css`
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`;

const getMaskImage = (left, right) => {
  if (left && right) {
    return "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)";
  }
  if (left) {
    return "linear-gradient(to right, transparent 0%, black 8%, black 100%)";
  }
  if (right) {
    return "linear-gradient(to right, black 0%, black 92%, transparent 100%)";
  }
  return "none";
};

const TruncatedTabName = ({name}) => {
  const tabNameRef = React.useRef(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const el = tabNameRef?.current;
    if (el) {
      setIsTruncated(el.offsetWidth < el.scrollWidth);
    }
  }, [name]);

  return (
      <Tooltip text={name} disable={!isTruncated} position="bottom" maxWidth={400} usePortal wrap>
        <span ref={tabNameRef} css={tabNameStyle}>{name}</span>
      </Tooltip>
  );
};

const TabBar = React.memo(({
  tabs,
  activeTabId,
  isEditing,
  isGamifiedDashboard,
  aspectRatio,
  tabIdToLoadStatus,
  onTabChange,
  onAddTab,
  onDeleteTab,
  onRenameTab,
  onUpdateTab,
  onCloneTab,
  onMoveTab,
  onViewCurrent,
  onPresentFromTab,
  onCopyPresentationLink,
  presentationLinkUrl,
  presentationSecondsRemaining,
  isPresentationMode,
  onOpenDashboardSettings,
  copyTargetDashboards = Immutable.List(),
  hasUnsavedChanges,
  onCopyTabToDashboard,
}) => {
  const {theme} = React.useContext(CustomThemeContext);

  // When every tab is skipped there's nothing to present, so the presentation options
  // are disabled (viewing the current tab is still allowed).
  const allTabsSkipped = tabs.size > 0 && tabs.every(t => t.get("skipInPresentation", false));

  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [menuTabId, setMenuTabId] = React.useState(null);
  const [changeSettingsTabId, setChangeSettingsTabId] = React.useState(null);
  const [renameValue, setRenameValue] = React.useState("");
  const [durationValue, setDurationValue] = React.useState(30);
  const [skipValue, setSkipValue] = React.useState(false);
  const [deleteTabId, setDeleteTabId] = React.useState(null);
  const [copyTabId, setCopyTabId] = React.useState(null);
  const [showUnsavedCopyWarning, setShowUnsavedCopyWarning] = React.useState(false);

  const tabsRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  React.useEffect(() => {
    const root = tabsRef.current;
    if (!root) {
      return;
    }
    const scroller = root.querySelector(".MuiTabs-scroller");
    if (!scroller) {
      return;
    }

    const update = () => {
      setCanScrollLeft(scroller.scrollLeft > 0);
      setCanScrollRight(
          Math.ceil(scroller.scrollLeft) + scroller.clientWidth < scroller.scrollWidth
      );
    };

    update();
    scroller.addEventListener("scroll", update);
    const observer = new ResizeObserver(update);
    observer.observe(scroller);

    return () => {
      scroller.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [tabs]);

  const sortedTabs = React.useMemo(
      () => tabs.sortBy(t => t.get("position")),
      [tabs]
  );

  const handleMenuOpen = (event, tabId) => {
    setMenuAnchor(event.currentTarget);
    setMenuTabId(tabId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuTabId(null);
  };

  const openSettingsDialog = (tabId) => {
    const tab = sortedTabs.find(t => t.get("id") === tabId);
    setChangeSettingsTabId(tabId);
    setRenameValue(tab ? tab.get("name") : "");
    setDurationValue(tab ? tab.get("presentationDuration", 30) : 30);
    setSkipValue(tab ? tab.get("skipInPresentation", false) : false);
    handleMenuClose();
  };

  const isDuplicateTabName = React.useMemo(() => {
    const trimmed = renameValue.trim().toLowerCase();

    if (changeSettingsTabId == null || !trimmed) {
      return false;
    }
    return sortedTabs.some(t => t.get("id") !== changeSettingsTabId && t.get("name").toLowerCase() === trimmed);
  }, [renameValue, changeSettingsTabId, sortedTabs]);

  const isDurationInvalid = isGamifiedDashboard
      && (durationValue === "" || !Number.isFinite(Number(durationValue)) || Number(durationValue) < 5);

  const handleSaveSettings = () => {
    if (changeSettingsTabId != null && renameValue.trim() && !isDuplicateTabName && !isDurationInvalid) {
      onRenameTab(changeSettingsTabId, renameValue.trim());
      if (isGamifiedDashboard && onUpdateTab) {
        onUpdateTab(changeSettingsTabId, {
          presentationDuration: Number(durationValue),
          skipInPresentation: skipValue,
        });
      }
    }
    setChangeSettingsTabId(null);
    setRenameValue("");
    setDurationValue(30);
    setSkipValue(false);
  };

  const openDeleteDialog = (tabId) => {
    setDeleteTabId(tabId);
    handleMenuClose();
  };

  // Copying navigates away from this dashboard, so anything unsaved here would be
  // lost. Block until the user has saved rather than silently discarding it.
  const openCopyDialog = (tabId) => {
    if (hasUnsavedChanges) {
      setShowUnsavedCopyWarning(true);
    } else {
      setCopyTabId(tabId);
    }
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (deleteTabId != null) {
      onDeleteTab(deleteTabId);
      setDeleteTabId(null);
    }
  };

  const activeTabPosition = sortedTabs.findIndex(t => t.get("id") === menuTabId);
  const isFirst = activeTabPosition === 0;
  const isLast = activeTabPosition === sortedTabs.size - 1;

  const dropdownMenuItems = [
    {
      icon: "bhi-save",
      label: "Set as first",
      onClick: () => onMoveTab(menuTabId, "first"),
      disabled: isFirst,
      transform: "rotate(90deg)"
    }, {
      icon: "bhi-arrow-left",
      label: "Move left",
      onClick: () => onMoveTab(menuTabId, "left"),
      disabled: isFirst
    }, {
      icon: "bhi-arrow-right",
      label: "Move right",
      onClick: () => onMoveTab(menuTabId, "right"),
      disabled: isLast
    }, {
      icon: "bhi-save",
      label: "Set as last",
      onClick: () => onMoveTab(menuTabId, "last"),
      disabled: isLast,
      transform: "rotate(270deg)"
    }, {
      icon: "bhi-duplicate",
      label: "Clone",
      onClick: () => {
        onCloneTab(menuTabId);
        handleMenuClose();
      }
    }, {
      icon: "bhi-dashboard",
      label: "Copy to...",
      testId: "tab-menu-copy-to",
      onClick: () => openCopyDialog(menuTabId)
    }, {
      icon: "bhi-configure-o",
      label: "Settings",
      onClick: () => openSettingsDialog(menuTabId)
    }, {
      icon: "bhi-delete",
      label: "Delete",
      onClick: () => openDeleteDialog(menuTabId)
    }
  ];

  return (
      <div css={tabBarContainerStyle(theme)} className="hide-for-print" data-test-id="tab-bar">
        {isGamifiedDashboard && (
            <FontAwesomeIcon
                icon={faTvRetro}
                style={{fontSize: "0.9rem", marginRight: 8, color: Colors.tvColor, flexShrink: 0}}
                title="Presentation mode available"
            />
        )}
        <Tabs
            ref={tabsRef}
            value={activeTabId}
            onChange={(e, tabId) => onTabChange(tabId)}
            variant="scrollable"
            scrollButtons
            css={tabsStyle}
            sx={{
              // Size to the tabs' content (but allow shrinking so scrolling still kicks
              // in when they overflow) so the add button / scroller sit right after the
              // last tab rather than being pushed to the far right by flex-grow.
              flex: "0 1 auto",
              minWidth: 0,
              "& .MuiTabs-scroller": {
                maskImage: getMaskImage(canScrollLeft, canScrollRight),
                WebkitMaskImage: getMaskImage(canScrollLeft, canScrollRight)
              },
              "& .MuiTabScrollButton-root.Mui-disabled": {
                opacity: 0.3
              },
              "& .MuiTabScrollButton-root:first-of-type": {
                width: "16px",
                marginTop: "2px"
              },
              "& .MuiTabScrollButton-root:last-of-type": {
                width: "16px",
                marginTop: "2px"
              }
            }}>
          {sortedTabs.map(tab => {
            const tabId = tab.get("id");
            const isActive = tabId === activeTabId;
            const status = tabIdToLoadStatus ? tabIdToLoadStatus.get(tabId) : null;
            const isLoadingStatus = status === TabLoadStatus.LOADING || status === TabLoadStatus.PARTIAL;
            return (
                <Tab
                    key={tabId}
                    value={tabId}
                    css={tabStyle(theme, isActive && isEditing)}
                    data-test-id={`tab-${tabId}`}
                    sx={{
                      ...(isActive && {
                        "&:active": {
                          boxShadow: "none"
                        }
                      })
                    }}
                    label={
                      <span style={{display: "flex", alignItems: "center", gap: 4}}>
                        {isLoadingStatus && (
                            <CircularProgress size={12} thickness={5} data-test-id={`tab-spinner-${tabId}`} />
                        )}
                        <TruncatedTabName name={tab.get("name")} />
                        {isGamifiedDashboard && (
                            tab.get("skipInPresentation", false)
                              ? <i
                                    className="bhi-hidden"
                                    title="Skipped in presentation"
                                    data-test-id={`tab-skipped-${tabId}`}
                                    style={{fontSize: 13, color: theme.palette.text.secondary, flexShrink: 0}} />
                              : <span
                                    data-test-id={`tab-duration-${tabId}`}
                                    style={{color: theme.palette.text.secondary, whiteSpace: "nowrap", flexShrink: 0}}>
                                  ({isActive && presentationSecondsRemaining != null
                                    ? presentationSecondsRemaining
                                    : tab.get("presentationDuration", 30)}s)
                                </span>
                        )}
                        {isActive && isEditing && (
                            <Button
                                style={{
                                  background: Boolean(menuAnchor)
                                      ? theme.palette.background.paper
                                      : "transparent",
                                  padding: 0,
                                  height: 20,
                                  boxShadow: "none",
                                  borderRadius: 5,
                                  flexShrink: 0,
                                  minWidth: 0
                                }}
                                data-test-id={`tab-menu-${tabId}`}
                                onClick={(e) => handleMenuOpen(e, tabId)}>
                              <i
                                  className="bhi-kebab-menu"
                                  style={{fontSize: 14, color: theme.palette.text.secondary}} />
                            </Button>
                        )}
                      </span>
                    }
                />
            );
          })}
        </Tabs>

        {isEditing && (
            <IconButton
                onClick={onAddTab}
                data-test-id="add-tab-button"
                sx={{
                  flexShrink: 0,
                  "&:hover": {
                    backgroundColor: "transparent"
                  },
                  "&:active": {
                    boxShadow: "none"
                  }
            }}>
              <i className="bhi-add-thin" style={{fontSize: "0.8rem", marginBottom: "2px"}} />
            </IconButton>
        )}

        {(isEditing || isGamifiedDashboard) && (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginLeft: "auto",
              flexShrink: 0,
            }}>
              {isGamifiedDashboard && (
                  <PresentationSplitButton
                      onViewCurrent={onViewCurrent}
                      onPresentFromTab={onPresentFromTab}
                      onCopyPresentationLink={onCopyPresentationLink}
                      presentationLinkUrl={presentationLinkUrl}
                      presentDisabled={allTabsSkipped}
                      iframeUnsavedDisabled={isInIframe && hasUnsavedChanges}
                      copyLinkDisabled={hasUnsavedChanges}
                  />
              )}
              {isEditing && (
                  <IconButton
                      size="small"
                      onClick={onOpenDashboardSettings}
                      data-test-id="dashboard-settings-button"
                      sx={{flexShrink: 0, "&:hover": {backgroundColor: "transparent"}, "&:active": {boxShadow: "none"}}}>
                    <i className="bhi-configure-o" style={{fontSize: "1rem", color: theme.palette.text.secondary}} />
                  </IconButton>
              )}
            </div>
        )}

        <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            PaperProps={{sx: {width: 160, backgroundColor: theme.palette.background.card}}}>
          {dropdownMenuItems.map(({icon, label, onClick, disabled, transform, testId}) =>
              <MenuItem key={label} onClick={onClick} disabled={disabled} data-test-id={testId}>
                <ListItemIcon style={{minWidth: 20}}>
                  <i className={icon} style={{fontSize: "0.6rem", transform}} />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{fontSize: "0.8rem"}}>{label}</ListItemText>
              </MenuItem>
          )}
        </Menu>

        <Cube19Dialog
            open={changeSettingsTabId !== null}
            onClose={() => setChangeSettingsTabId(null)}
            title="Settings"
            maxWidth="xs"
            actions={<div style={{display: "flex", gap: "8px", justifyContent: "flex-end", alignItems: "center"}}>
              <TextButton
                  label="Cancel"
                  onClick={() => setChangeSettingsTabId(null)}
              />
              <TextButton
                  type="primary"
                  label="Save"
                  onClick={handleSaveSettings}
                  disabled={!renameValue.trim() || isDuplicateTabName || isDurationInvalid}
              />
            </div>}>
          <p style={{marginBottom: 8}}>Tab name</p>
          <CubeTextField
              autoFocus
              fullWidth
              margin="dense"
              value={renameValue}
              onChange={(e) => setRenameValue(stripEmoji(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && renameValue.trim() && !isDuplicateTabName && !isDurationInvalid) {
                  handleSaveSettings();
                }
              }}
              testId="rename-tab-input"
              error={isDuplicateTabName}
              helperText={isDuplicateTabName ? "A tab with this name already exists" : undefined}
          />
          {isGamifiedDashboard && (
              <>
                <p style={{marginBottom: 8, marginTop: 16}}>Minimum seconds to display</p>
                <CubeTextField
                    fullWidth
                    margin="dense"
                    type="number"
                    value={durationValue}
                    onChange={(e) => setDurationValue(e.target.value)}
                    onKeyDown={(e) => {
                      // type="number" otherwise accepts "e", "+", "-" and "." — block them
                      // so only whole seconds can be entered.
                      if (["e", "E", "+", "-", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    inputProps={{min: 5, step: 1}}
                    error={isDurationInvalid}
                    helperText={isDurationInvalid ? "Must be at least 5 seconds" : undefined}
                    testId="presentation-duration-input"
                />
                <div style={{display: "flex", alignItems: "center", marginTop: 12}}>
                  <input
                      type="checkbox"
                      id="skip-in-presentation"
                      checked={skipValue}
                      onChange={(e) => setSkipValue(e.target.checked)}
                      data-test-id="skip-in-presentation-checkbox"
                      style={{marginRight: 8, cursor: "pointer"}}
                  />
                  <label htmlFor="skip-in-presentation" style={{cursor: "pointer", fontSize: "0.9rem"}}>
                    Skip this tab in presentation mode
                  </label>
                </div>
              </>
          )}
        </Cube19Dialog>

        <Cube19Dialog
            open={deleteTabId !== null}
            onClose={() => setDeleteTabId(null)}
            title="Delete Tab"
            maxWidth="xs"
            actions={<div style={{display: "flex", gap: "8px", justifyContent: "flex-end", alignItems: "center"}}>
              <TextButton
                  label="Cancel"
                  onClick={() => setDeleteTabId(null)}
              />
              <TextButton
                  type="alert"
                  label="Delete"
                  onClick={handleDeleteConfirm}
              />
            </div>}>
          <div style={{display: "flex", flexDirection: "column", gap: "16px", alignItems: "center"}}>
            <svg width="58" height="58" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#F59E0B" strokeWidth="2" fill="none" />
              <line x1="12" y1="8" x2="12" y2="12" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="16" r="1" fill="#F59E0B" />
            </svg>
            Are you sure you want to delete this tab? All panels on this tab will be removed.
          </div>
        </Cube19Dialog>

        <CopyTabDialog
            open={copyTabId !== null}
            tabName={sortedTabs.find(t => t.get("id") === copyTabId)?.get("name") ?? ""}
            dashboards={copyTargetDashboards}
            onCancel={() => setCopyTabId(null)}
            onConfirm={(dashboardId) => {
              const tabId = copyTabId;
              setCopyTabId(null);
              onCopyTabToDashboard(tabId, dashboardId);
            }}
        />

        <Cube19Dialog
            open={showUnsavedCopyWarning}
            onClose={() => setShowUnsavedCopyWarning(false)}
            title="Unsaved Changes"
            maxWidth="xs"
            actions={<div style={{display: "flex", gap: "8px", justifyContent: "flex-end", alignItems: "center"}}>
              <TextButton
                  type="primary"
                  label="Close"
                  onClick={() => setShowUnsavedCopyWarning(false)}
              />
            </div>}>
          <div data-test-id="copy-tab-unsaved-dialog">
            Save this dashboard before copying a tab to another dashboard.
          </div>
        </Cube19Dialog>
      </div>
  );
});

export default TabBar;
